import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { ConfirmProvider } from './context/ConfirmContext';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { Layout } from './components/layout/Layout';
import { Loader } from './components/common/Loader';
import { BrandLoader } from './components/common/BrandLoader';
import { ScrollToTop } from './components/common/ScrollToTop';
import CookieBanner from './components/CookieBanner';
import OnboardingModal from './components/OnboardingModal';
import { Toaster } from 'react-hot-toast';

import { buildLoginUrl } from './utils/navigation';
import { AuthInterceptor } from './components/auth/AuthInterceptor';
import { NavigationProgressBar } from './components/common/NavigationProgressBar';

// Robust lazy loader that automatically reloads the page once if a code chunk fails to load after a deployment
function lazyWithRetry<T extends React.ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>
) {
  return React.lazy(async () => {
    try {
      const component = await componentImport();
      window.sessionStorage.removeItem('chunk-retry-refreshed');
      return component;
    } catch (error: any) {
      const isChunkError =
        error?.name === 'ChunkLoadError' ||
        error?.message?.includes('Loading chunk') ||
        error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('dynamically imported module');

      const alreadyRefreshed = window.sessionStorage.getItem('chunk-retry-refreshed');
      if (isChunkError && !alreadyRefreshed) {
        window.sessionStorage.setItem('chunk-retry-refreshed', 'true');
        window.location.reload();
      }
      throw error;
    }
  });
}

// Lazy loaded pages
const SignIn = lazyWithRetry(() => import('./pages/SignIn'));
const SignUp = lazyWithRetry(() => import('./pages/SignUp'));
const ForgotPassword = lazyWithRetry(() => import('./pages/ForgotPassword'));
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'));
const VerifyEmail = lazyWithRetry(() => import('./pages/VerifyEmail'));
const Explore = lazyWithRetry(() => import('./pages/Explore'));
const AniExplore = lazyWithRetry(() => import('./pages/AniExplore'));
const ArtworkDetail = lazyWithRetry(() => import('./pages/ArtworkDetail'));
const PostArtwork = lazyWithRetry(() => import('./pages/PostArtwork'));
const Cart = lazyWithRetry(() => import('./pages/Cart'));
const Wishlist = lazyWithRetry(() => import('./pages/Wishlist'));
const Checkout = lazyWithRetry(() => import('./pages/Checkout'));
const Orders = lazyWithRetry(() => import('./pages/Orders'));
const Profile = lazyWithRetry(() => import('./pages/Profile'));
const EditProfile = lazyWithRetry(() => import('./pages/EditProfile'));
const Promotion = lazyWithRetry(() => import('./pages/Promotion'));
const Notifications = lazyWithRetry(() => import('./pages/Notifications'));
const Messages = lazyWithRetry(() => import('./pages/Messages'));
const Transactions = lazyWithRetry(() => import('./pages/Transactions'));
const Addresses = lazyWithRetry(() => import('./pages/Addresses'));
const SellerJoin = lazyWithRetry(() => import('./pages/SellerJoin'));
const Referrals = lazyWithRetry(() => import('./pages/Referrals'));
const Support = lazyWithRetry(() => import('./pages/Support'));
const Contact = lazyWithRetry(() => import('./pages/Contact'));
const AdminDashboard = lazyWithRetry(() => import('./pages/Admin/Dashboard'));
const ManageUsers = lazyWithRetry(() => import('./pages/Admin/ManageUsers'));
const SellerRequests = lazyWithRetry(() => import('./pages/Admin/SellerRequests'));
const ManageArtworks = lazyWithRetry(() => import('./pages/Admin/ManageArtworks'));
const ManageMemberships = lazyWithRetry(() => import('./pages/Admin/ManageMemberships'));
const Reports = lazyWithRetry(() => import('./pages/Admin/Reports'));
const Payouts = lazyWithRetry(() => import('./pages/Admin/Payouts'));
const Commission = lazyWithRetry(() => import('./pages/Admin/Commission'));
const Financials = lazyWithRetry(() => import('./pages/Admin/Financials'));
const ManageOrders = lazyWithRetry(() => import('./pages/Admin/ManageOrders'));
const ManageCodes = lazyWithRetry(() => import('./pages/Admin/ManageCodes'));
const ManageCoupons = lazyWithRetry(() => import('./pages/Admin/ManageCoupons'));
const Settings = lazyWithRetry(() => import('./pages/Admin/Settings'));
const AdminNotifications = lazyWithRetry(() => import('./pages/Admin/Notifications'));
const AdminSupport = lazyWithRetry(() => import('./pages/Admin/Support'));
const ManageFrames = lazyWithRetry(() => import('./pages/Admin/ManageFrames'));
const SellerAddressManagement = lazyWithRetry(() => import('./pages/Admin/SellerAddressManagement'));
const ShiprocketOrderManagement = lazyWithRetry(() => import('./pages/Admin/ShiprocketOrderManagement'));
const CustomerDashboard = lazyWithRetry(() => import('./pages/Customer/Dashboard'));
const SellerDashboard = lazyWithRetry(() => import('./pages/Seller/Dashboard'));
const SellerOrders = lazyWithRetry(() => import('./pages/Seller/Orders'));
const AcceptOrder = lazyWithRetry(() => import('./pages/Seller/AcceptOrder'));
const OrderDetail = lazyWithRetry(() => import('./pages/Seller/OrderDetail'));
const SellerProducts = lazyWithRetry(() => import('./pages/Seller/Products'));
const EditArtwork = lazyWithRetry(() => import('./pages/Seller/EditArtwork'));
const SellerRevenue = lazyWithRetry(() => import('./pages/Seller/Revenue'));
const SellerCustomers = lazyWithRetry(() => import('./pages/Seller/Customers'));
const SellerReviews = lazyWithRetry(() => import('./pages/Seller/Reviews'));
const SellerFollowers = lazyWithRetry(() => import('./pages/Seller/Followers'));
const SellerStore = lazyWithRetry(() => import('./pages/Seller/Store'));
const SellerBankDetails = lazyWithRetry(() => import('./pages/Seller/BankDetails'));
const SellerSettings = lazyWithRetry(() => import('./pages/Seller/Settings'));
const AddPhone = lazyWithRetry(() => import('./pages/AddPhone'));
const PrivacyPolicy = lazyWithRetry(() => import('./pages/PrivacyPolicy'));
const About = lazyWithRetry(() => import('./pages/About'));
const Terms = lazyWithRetry(() => import('./pages/Terms'));
const PlatformFees = lazyWithRetry(() => import('./pages/PlatformFees'));
const SellerSLA = lazyWithRetry(() => import('./pages/SellerSLA'));
const Sitemap = lazyWithRetry(() => import('./pages/Sitemap'));

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <BrandLoader />;
  if (!user) {
    const currentUrl = location.pathname + location.search;
    return <Navigate to={buildLoginUrl(currentUrl)} replace />;
  }
  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, fetchUser } = useAuth();
  const location = useLocation();

  React.useEffect(() => {
    if (user && typeof fetchUser === 'function') {
      fetchUser();
    }
  }, [location.pathname]);

  if (loading && !user) return <BrandLoader />;
  if (!user && !loading) {
    const currentUrl = location.pathname + location.search;
    return <Navigate to={buildLoginUrl(currentUrl)} replace />;
  }
  if (user && !user.isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const SellerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, fetchUser } = useAuth();
  const location = useLocation();

  React.useEffect(() => {
    if (user && typeof fetchUser === 'function') {
      fetchUser();
    }
  }, [location.pathname]);

  if (loading && !user) return <BrandLoader />;
  if (!user && !loading) {
    const currentUrl = location.pathname + location.search;
    return <Navigate to={buildLoginUrl(currentUrl)} replace />;
  }
  if (user && !user.isPromoted && !user.isAdmin) {
    const currentUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/promotion?returnTo=${currentUrl}`} replace />;
  }
  return <>{children}</>;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) return <BrandLoader />;

  return (
    <Suspense fallback={<BrandLoader />}>
      <Routes>
        <Route path="/signin" element={!user ? <SignIn /> : <Navigate to="/" replace />} />
        <Route path="/signup" element={!user ? <SignUp /> : <Navigate to="/" replace />} />
        <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/" replace />} />
        <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to="/" replace />} />
        <Route path="/verify-email" element={!user ? <VerifyEmail /> : <Navigate to="/" replace />} />
        <Route path="/" element={<Layout><Explore /></Layout>} />
        <Route path="/explore" element={<Layout><AniExplore /></Layout>} />
        <Route path="/feed" element={<Layout><AniExplore /></Layout>} />
        <Route path="/trending" element={<Layout><AniExplore /></Layout>} />
        <Route path="/artwork/:id" element={<Layout showBottomNav={false}><ArtworkDetail /></Layout>} />
        <Route path="/post" element={<ProtectedRoute><Layout showBottomNav={false}><PostArtwork /></Layout></ProtectedRoute>} />
        <Route path="/cart" element={<ProtectedRoute><Layout><Cart /></Layout></ProtectedRoute>} />
        <Route path="/wishlist" element={<ProtectedRoute><Layout><Wishlist /></Layout></ProtectedRoute>} />
        <Route path="/checkout" element={<ProtectedRoute><Layout><Checkout /></Layout></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute><Layout><Orders /></Layout></ProtectedRoute>} />
        <Route path="/orders/:orderId" element={<ProtectedRoute><Layout><Orders /></Layout></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Layout><Profile /></Layout></ProtectedRoute>} />
        <Route path="/profile/:userId" element={<ProtectedRoute><Layout><Profile /></Layout></ProtectedRoute>} />
        <Route path="/profile/edit" element={<ProtectedRoute><Layout showBottomNav={false}><EditProfile /></Layout></ProtectedRoute>} />
        <Route path="/edit-profile" element={<Navigate to="/profile/edit" replace />} />
        <Route path="/promotion" element={<ProtectedRoute><Layout><Promotion /></Layout></ProtectedRoute>} />
        <Route path="/seller/join" element={<Layout showBottomNav={false}><SellerJoin /></Layout>} />
        <Route path="/notifications" element={<ProtectedRoute><Layout><Notifications /></Layout></ProtectedRoute>} />
        <Route path="/messages" element={<Navigate to="/support" replace />} />
        <Route path="/transactions" element={<ProtectedRoute><Layout><Transactions /></Layout></ProtectedRoute>} />
        <Route path="/addresses" element={<ProtectedRoute><Layout><Addresses /></Layout></ProtectedRoute>} />
        <Route path="/referrals" element={<ProtectedRoute><Layout><Referrals /></Layout></ProtectedRoute>} />
        <Route path="/support" element={<ProtectedRoute><Layout><Support /></Layout></ProtectedRoute>} />
        <Route path="/contact" element={<Layout><Contact /></Layout>} />
        
        <Route path="/customer" element={<ProtectedRoute><CustomerDashboard /></ProtectedRoute>} />
        <Route path="/customer/dashboard" element={<Navigate to="/customer" replace />} />
        <Route path="/seller" element={<SellerRoute><SellerDashboard /></SellerRoute>} />
        <Route path="/seller/dashboard" element={<Navigate to="/seller" replace />} />
        <Route path="/seller/orders" element={<SellerRoute><Layout><SellerOrders /></Layout></SellerRoute>} />
        <Route path="/seller/orders/:orderId/accept" element={<SellerRoute><Layout showBottomNav={false}><AcceptOrder /></Layout></SellerRoute>} />
        <Route path="/seller/orders/:orderId" element={<SellerRoute><Layout showBottomNav={false}><OrderDetail /></Layout></SellerRoute>} />
        <Route path="/seller/products" element={<SellerRoute><Layout><SellerProducts /></Layout></SellerRoute>} />
        <Route path="/edit-artwork/:id" element={<SellerRoute><Layout showBottomNav={false}><EditArtwork /></Layout></SellerRoute>} />
        <Route path="/seller/artworks/edit/:id" element={<SellerRoute><Layout showBottomNav={false}><EditArtwork /></Layout></SellerRoute>} />
        <Route path="/seller/products/edit/:id" element={<SellerRoute><Layout showBottomNav={false}><EditArtwork /></Layout></SellerRoute>} />
        <Route path="/seller/edit-artwork/:id" element={<SellerRoute><Layout showBottomNav={false}><EditArtwork /></Layout></SellerRoute>} />
        <Route path="/seller/revenue" element={<SellerRoute><Layout><SellerRevenue /></Layout></SellerRoute>} />
        <Route path="/seller/customers" element={<SellerRoute><Layout><SellerCustomers /></Layout></SellerRoute>} />
        <Route path="/seller/reviews" element={<SellerRoute><Layout><SellerReviews /></Layout></SellerRoute>} />
        <Route path="/seller/followers" element={<SellerRoute><Layout><SellerFollowers /></Layout></SellerRoute>} />
        <Route path="/seller/store" element={<SellerRoute><Layout><SellerStore /></Layout></SellerRoute>} />
        <Route path="/seller/bank" element={<SellerRoute><Layout><SellerBankDetails /></Layout></SellerRoute>} />
        <Route path="/seller/settings" element={<Navigate to="/seller/bank" replace />} />
        
        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><ManageUsers /></AdminRoute>} />
        <Route path="/admin/seller-requests" element={<AdminRoute><SellerRequests /></AdminRoute>} />
        <Route path="/admin/orders" element={<AdminRoute><ManageOrders /></AdminRoute>} />
        <Route path="/admin/artworks" element={<AdminRoute><ManageArtworks /></AdminRoute>} />
        <Route path="/admin/memberships" element={<AdminRoute><ManageMemberships /></AdminRoute>} />
        <Route path="/admin/codes" element={<AdminRoute><ManageCodes /></AdminRoute>} />
        <Route path="/admin/coupons" element={<AdminRoute><ManageCoupons /></AdminRoute>} />
        <Route path="/admin/reports" element={<AdminRoute><Reports /></AdminRoute>} />
        <Route path="/admin/payouts" element={<AdminRoute><Payouts /></AdminRoute>} />
        <Route path="/admin/commission" element={<AdminRoute><Commission /></AdminRoute>} />
        <Route path="/admin/financials" element={<AdminRoute><Financials /></AdminRoute>} />
        <Route path="/admin/notifications" element={<AdminRoute><AdminNotifications /></AdminRoute>} />
        <Route path="/admin/support" element={<AdminRoute><AdminSupport /></AdminRoute>} />
        <Route path="/admin/settings" element={<AdminRoute><Settings /></AdminRoute>} />
        <Route path="/admin/frames" element={<AdminRoute><ManageFrames /></AdminRoute>} />
        <Route path="/admin/seller-addresses" element={<AdminRoute><SellerAddressManagement /></AdminRoute>} />
        <Route path="/admin/shipments" element={<AdminRoute><ShiprocketOrderManagement /></AdminRoute>} />
        <Route path="/add-phone" element={<ProtectedRoute><AddPhone /></ProtectedRoute>} />
        
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/about" element={<About />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/fees" element={<PlatformFees />} />
        <Route path="/sla" element={<SellerSLA />} />
        <Route path="/sitemap" element={<Sitemap />} />
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  const { user } = useAuth();

  React.useEffect(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const couponParam = searchParams.get('coupon');
      if (couponParam) {
        localStorage.setItem('ani_pending_coupon', couponParam.trim().toUpperCase());
      }
    } catch (_) {}
  }, []);

  return (
    <>
      <BrowserRouter>
        <NavigationProgressBar />
        <ScrollToTop />
        <Toaster position="top-center" reverseOrder={false} />
        <AuthInterceptor>
          <AppRoutes />
        </AuthInterceptor>
        <CookieBanner />
        {user && !user.isOnboarded && (
          <OnboardingModal onComplete={() => {}} />
        )}
      </BrowserRouter>
    </>
  );
}

const GOOGLE_CLIENT_ID = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '788466113309-1ant2kv2dp37focqoiuha5c1ri8qm4un.apps.googleusercontent.com')
  .replace(/^["']|["']$/g, '')
  .trim();

function AppWrapper() {
  return (
    <HelmetProvider>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <SettingsProvider>
            <ConfirmProvider>
              <CartProvider>
                <WishlistProvider>
                  <App />
                </WishlistProvider>
              </CartProvider>
            </ConfirmProvider>
          </SettingsProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

export default AppWrapper;
